import axios from 'axios';
import moment from 'moment';
// Interfaces
import { VisitasState, ClienteAPI, Clientes, FetchVisitasResponse, ClienteVentaAnterior } from '@/models/StateVisitas';

const BASE_URL = 'http://localhost:3001/api/public';

const dias = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const state = (): VisitasState => ({
	tableData: [],
	diaIndex: 0, //Indice del dia actual mostrado
	diaIndexHoy: 0, // Indice del dia real(hoy)
	diasAtras: 0, //Dias hacia atras en modo historico
	modoHistorico: false,
	turnosDelDia: [],
	semanaOffset: 0, //Cuantas semanas fui para adelante
});
const getters = {
	diaAmigable: (state: VisitasState, getters) => {
		if (state.modoHistorico) {
			// En modo histórico solo usamos díasAtras
			if (state.diasAtras === 1) return 'Ayer';
			else if (state.diasAtras === 2) return 'Antes de ayer';
			else {
				const fecha = moment().subtract(state.diasAtras, 'days');
				return fecha.format('DD [de] MMMM'); // ej: "13 de mayo"
			}
		} else {
			// En modo normal usamos diaIndex, diaIndexHoy y semanaOffset
			const diffDias = state.diaIndex - state.diaIndexHoy + state.semanaOffset * 7;

			if (diffDias === 0) return 'Hoy';
			else if (diffDias === 1) return 'Mañana';
			else if (diffDias === 2) return 'Pasado mañana';
			else {
				const fecha = moment(getters.fechaSeleccionada, 'YYYY-MM-DD');
				return fecha.format('DD [de] MMMM');
			}
		}
	},
	diaHoy: (state: VisitasState) => dias[state.diaIndex],
	clientesPorTurno: (
		state: VisitasState
	): {
		pendientes: { manana: ClienteAPI[]; tarde: ClienteAPI[] };
		completados: { manana: ClienteAPI[]; tarde: ClienteAPI[] };
		nuevos: ClienteAPI[];
	} => {
		const pendientes = { manana: [] as ClienteAPI[], tarde: [] as ClienteAPI[] };
		const completados = { manana: [] as ClienteAPI[], tarde: [] as ClienteAPI[] };
		const nuevos: ClienteAPI[] = [];

		state.tableData.forEach((cliente: ClienteAPI) => {
			if (cliente.es_nuevo) nuevos.push(cliente);
			else if (cliente.atendido) {
				if (cliente.turno === 'mañana') completados.manana.push(cliente);
				else if (cliente.turno === 'tarde') completados.tarde.push(cliente);
			} else {
				if (cliente.turno === 'mañana') pendientes.manana.push(cliente);
				else if (cliente.turno === 'tarde') pendientes.tarde.push(cliente);
			}
		});

		return { pendientes, completados, nuevos };
	},
	//Calcula la fecha restando dias atras al dia de hoy
	fechaHistorica: (state: VisitasState) => {
		const fecha = moment().subtract(state.diasAtras, 'days');
		return fecha.format('YYYY-MM-DD');
	},
	tableData: (state: VisitasState) => state.tableData,
	modoHistorico: (state: VisitasState) => state.modoHistorico,
	//Calcula el dia con el indice, es la que determina que dia esta el cliente cuando va hacia delante en los dias de la semana
	fechaSeleccionada: (state: VisitasState) => {
		const diferenciaDias = state.diaIndex - state.diaIndexHoy + state.semanaOffset * 7;
		const fecha = moment().add(diferenciaDias, 'days');
		return fecha.format('YYYY-MM-DD');
	},
	//Dice si estas viendo el dia actual
	esHoy: (state: VisitasState, getters) => {
		const hoy = moment().format('YYYY-MM-DD');
		return state.modoHistorico ? getters.fechaHistorica === hoy : getters.fechaSeleccionada === hoy;
	},
	esFuturo: (state: VisitasState, getters) => {
		const hoy = moment().format('YYYY-MM-DD');
		return getters.fechaSeleccionada > hoy && !state.modoHistorico;
	},
};
const mutations = {
	RESET_DIA(state: VisitasState) {
		state.tableData = [];
		state.turnosDelDia = [];
	},
	SET_SEMANA_OFFSET(state: VisitasState, offset: number) {
		state.semanaOffset = offset;
	},
	SET_DIA_INDEX(state: VisitasState, index: number) {
		state.diaIndex = index;
	},
	SET_DIA_INDEX_HOY(state: VisitasState, index: number) {
		state.diaIndexHoy = index;
	},
	SET_DIAS_ATRAS(state: VisitasState, value: number) {
		state.diasAtras = value;
	},
	SET_MODO_HISTORICO(state: VisitasState, value: boolean) {
		state.modoHistorico = value;
	},
	SET_TABLE_DATA(state: VisitasState, data: ClienteAPI[]) {
		state.tableData = data;
	},
	SET_TURNOS_DEL_DIA(state: VisitasState, turnos: string[]) {
		state.turnosDelDia = turnos;
	},
};
const actions = {
	async actualizarEstado({ state, getters }, { clienteId, nuevoEstado }) {
		try {
			const cliente: Clientes = state.tableData.find((c: Clientes) => c.generalId === clienteId || c.id === clienteId);
			if (!cliente) return;

			const payload = {
				fecha: getters.esHoy ? moment().format('YYYY-MM-DD') : getters.fechaSeleccionada,
				codent_id: cliente.id,
				estado: nuevoEstado,
			};

			await axios.patch(`${BASE_URL}/customers/estado`, payload);
			cliente.estado = nuevoEstado;
		} catch (error) {
			console.error('Error actualizando estado', error);
			throw error;
		}
	},
	resetDia({ commit }) {
		commit('RESET_DIA');
	},
	async inicializarDiaActual({ commit }) {
		const hoy = moment();
		const dia = hoy.isoWeekday();
		const index = dia - 1;
		commit('SET_DIA_INDEX', index);
		commit('SET_DIA_INDEX_HOY', index);
	},
	//Esta es la llegada de los datos de la vista de hoy
	async fetchVisitas({ commit, getters }) {
		try {
			const response = await axios.get<FetchVisitasResponse>(`${BASE_URL}/visitas/hoy?day=${getters.diaHoy}`);
			const { clientes, turnos } = response.data;
			const ordenDeseado = ['Mañana', 'Tarde'];
			const turnosOrdenados = turnos
				.map((t: string) => t.trim())
				.filter((v, i, arr) => arr.indexOf(v) === i)
				.sort((a, b) => ordenDeseado.indexOf(a) - ordenDeseado.indexOf(b));

			const mapped: Clientes[] = clientes.map(
				(v: ClienteAPI): Clientes => ({
					key_id: v.ventas_id, //Id de test_ventas
					generalId: v.id, //Id de dim_entidades_visitas
					id: v.codenti_id, //Id de dim_entidades
					nombre: v.nombre,
					direccion: v.lugar_entrega || 'Sin dirección',
					turno: (v.turno || '').trim().toLowerCase(),
					documento: v.num_documento,
					atendido: v.estado_venta === 'realizado',
					es_nuevo: v.es_nuevo,
					hora: v.hora,
					detalle: v.detalle,
					estado: v.estado,
				})
			);
			// Solo mostrar esNuevo si es hoy
			const soloHoy = moment().format('YYYY-MM-DD');
			const esHoy = getters.fechaSeleccionada === soloHoy;

			const filtrados: Clientes[] = esHoy ? mapped : mapped.filter((cliente: Clientes) => !cliente.es_nuevo);
			commit('SET_TABLE_DATA', filtrados);
			commit('SET_TURNOS_DEL_DIA', turnosOrdenados);
		} catch (err) {
			console.error('Error al cargar visitas:', err);
		}
	},
	async fetchVentasAnteriores({ commit, getters }) {
		const fecha = getters.fechaHistorica;
		try {
			const response = await axios.get<ClienteVentaAnterior[]>(`${BASE_URL}/visitas/fecha?date=${fecha}`);
			const mapped = response.data.map(v => ({
				id: v.id,
				nombre: v.nombre,
				direccion: v.lugar_entrega || v.direccion || 'Sin direccion',
			}));
			commit('SET_TABLE_DATA', mapped);
		} catch (err) {
			console.error('Error al cargar ventas pasadas:', err);
		}
	},
	//Estas dos se encargan de moverte entre dias
	async diaSiguiente({ state, commit, dispatch }) {
		await dispatch('resetDia');
		if (state.modoHistorico) {
			if (state.diasAtras > 1) {
				commit('SET_DIAS_ATRAS', state.diasAtras - 1);
				dispatch('fetchVentasAnteriores');
			} else {
				commit('SET_MODO_HISTORICO', false);
				commit('SET_DIAS_ATRAS', 0);
				commit('SET_DIA_INDEX', state.diaIndexHoy);
				dispatch('fetchVisitas');
			}
		} else {
			let nuevoDiaIndex = state.diaIndex + 1;
			let nuevaSemanaOffset = state.semanaOffset;

			if (nuevoDiaIndex > 6) {
				nuevoDiaIndex = 0;
				nuevaSemanaOffset += 1;
			}
			commit('SET_DIA_INDEX', nuevoDiaIndex);
			commit('SET_SEMANA_OFFSET', nuevaSemanaOffset);
			dispatch('fetchVisitas');
		}
	},
	async diaAnterior({ state, commit, dispatch }) {
		await dispatch('resetDia');
		if (state.modoHistorico) {
			commit('SET_DIAS_ATRAS', state.diasAtras + 1);
			dispatch('fetchVentasAnteriores');
		} else {
			let nuevoDiaIndex = state.diaIndex - 1;
			let nuevaSemanaOffset = state.semanaOffset;

			if (nuevoDiaIndex < 0) {
				nuevoDiaIndex = 6;
				nuevaSemanaOffset -= 1;
			}
			// No permitas ir antes del día actual sin modo histórico
			const totalDiff = nuevoDiaIndex - state.diaIndexHoy + nuevaSemanaOffset * 7;
			if (totalDiff < 0) {
				commit('SET_DIAS_ATRAS', Math.abs(totalDiff));
				commit('SET_MODO_HISTORICO', true);
				dispatch('fetchVentasAnteriores');
			} else {
				commit('SET_DIA_INDEX', nuevoDiaIndex);
				commit('SET_SEMANA_OFFSET', nuevaSemanaOffset);
				dispatch('fetchVisitas');
			}
		}
	},
};
export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
};
